
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Shield, TrendingUp, Target, Download, Plus, Filter } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Risk {
  id: string;
  title: string;
  category: string;
  description: string;
  probability: number;
  impact: number;
  riskScore: number;
  status: 'open' | 'mitigated' | 'closed' | 'accepted';
  owner: string;
  dateIdentified: string;
  mitigationPlan: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

export default function RiskTracker() {
  const [risks, setRisks] = useState<Risk[]>([
    {
      id: '1',
      title: 'Budget Overrun Risk',
      category: 'Financial',
      description: 'Potential cost escalation due to scope changes',
      probability: 3,
      impact: 4,
      riskScore: 12,
      status: 'open',
      owner: 'John Smith',
      dateIdentified: '2024-01-15',
      mitigationPlan: 'Implement strict change control process',
      priority: 'high'
    },
    {
      id: '2',
      title: 'Resource Availability',
      category: 'Resources',
      description: 'Key personnel might not be available during critical phases',
      probability: 4,
      impact: 3,
      riskScore: 12,
      status: 'mitigated',
      owner: 'Sarah Johnson',
      dateIdentified: '2024-01-10',
      mitigationPlan: 'Cross-train team members and maintain backup resources',
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Technology Integration Risk',
      category: 'Technical',
      description: 'New systems may not integrate properly with existing infrastructure',
      probability: 2,
      impact: 5,
      riskScore: 10,
      status: 'open',
      owner: 'Mike Davis',
      dateIdentified: '2024-01-20',
      mitigationPlan: 'Conduct thorough testing and proof of concept',
      priority: 'high'
    },
    {
      id: '4',
      title: 'Regulatory Compliance',
      category: 'Compliance',
      description: 'Changes in regulations might affect project deliverables',
      probability: 2,
      impact: 4,
      riskScore: 8,
      status: 'accepted',
      owner: 'Emma Wilson',
      dateIdentified: '2024-01-05',
      mitigationPlan: 'Monitor regulatory changes and maintain compliance buffer',
      priority: 'medium'
    }
  ]);

  const [filter, setFilter] = useState({
    status: 'all',
    category: 'all',
    priority: 'all'
  });

  const [newRisk, setNewRisk] = useState({
    title: '',
    category: '',
    description: '',
    probability: 1,
    impact: 1,
    owner: '',
    mitigationPlan: ''
  });

  const getRiskLevel = (score: number) => {
    if (score >= 15) return { level: 'Critical', color: 'bg-red-100 text-red-800' };
    if (score >= 10) return { level: 'High', color: 'bg-orange-100 text-orange-800' };
    if (score >= 6) return { level: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
    return { level: 'Low', color: 'bg-green-100 text-green-800' };
  };

  const getStatusColor = (status: Risk['status']) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'mitigated': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-green-100 text-green-800';
      case 'accepted': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredRisks = risks.filter(risk => {
    if (filter.status !== 'all' && risk.status !== filter.status) return false;
    if (filter.category !== 'all' && risk.category !== filter.category) return false;
    if (filter.priority !== 'all' && risk.priority !== filter.priority) return false;
    return true;
  });

  const riskStats = {
    total: risks.length,
    open: risks.filter(r => r.status === 'open').length,
    mitigated: risks.filter(r => r.status === 'mitigated').length,
    closed: risks.filter(r => r.status === 'closed').length,
    highRisk: risks.filter(r => r.riskScore >= 10).length
  };

  const averageRiskScore = risks.reduce((sum, risk) => sum + risk.riskScore, 0) / risks.length;

  const addRisk = () => {
    if (newRisk.title && newRisk.category && newRisk.owner) {
      const riskScore = newRisk.probability * newRisk.impact;
      const priority = riskScore >= 15 ? 'critical' : riskScore >= 10 ? 'high' : riskScore >= 6 ? 'medium' : 'low';
      
      const risk: Risk = {
        id: Date.now().toString(),
        title: newRisk.title,
        category: newRisk.category,
        description: newRisk.description,
        probability: newRisk.probability,
        impact: newRisk.impact,
        riskScore,
        status: 'open',
        owner: newRisk.owner,
        dateIdentified: new Date().toISOString().split('T')[0],
        mitigationPlan: newRisk.mitigationPlan,
        priority: priority as Risk['priority']
      };
      
      setRisks([...risks, risk]);
      setNewRisk({
        title: '',
        category: '',
        description: '',
        probability: 1,
        impact: 1,
        owner: '',
        mitigationPlan: ''
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-primary" />
            Risk Tracker
          </h1>
          <p className="text-muted-foreground">
            Comprehensive risk management and monitoring across all project areas
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="risks">Risk Register</TabsTrigger>
            <TabsTrigger value="matrix">Risk Matrix</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{riskStats.total}</div>
                    <p className="text-sm text-muted-foreground">Total Risks</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{riskStats.open}</div>
                    <p className="text-sm text-muted-foreground">Open Risks</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{riskStats.mitigated}</div>
                    <p className="text-sm text-muted-foreground">Mitigated</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{riskStats.highRisk}</div>
                    <p className="text-sm text-muted-foreground">High Risk</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{averageRiskScore.toFixed(1)}</div>
                    <p className="text-sm text-muted-foreground">Avg Risk Score</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Risk Distribution by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Financial', 'Technical', 'Resources', 'Compliance'].map((category) => {
                      const categoryRisks = risks.filter(r => r.category === category);
                      const percentage = (categoryRisks.length / risks.length) * 100;
                      
                      return (
                        <div key={category} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="font-medium">{category}</span>
                            <span>{categoryRisks.length} risks ({percentage.toFixed(0)}%)</span>
                          </div>
                          <Progress value={percentage} className="w-full" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Top Priority Risks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {risks
                      .sort((a, b) => b.riskScore - a.riskScore)
                      .slice(0, 4)
                      .map((risk) => {
                        const riskLevel = getRiskLevel(risk.riskScore);
                        return (
                          <div key={risk.id} className="p-3 border rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-sm">{risk.title}</h4>
                                <p className="text-xs text-muted-foreground mt-1">{risk.category}</p>
                                <p className="text-xs text-muted-foreground">Owner: {risk.owner}</p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Badge className={riskLevel.color}>
                                  {riskLevel.level}
                                </Badge>
                                <span className="text-xs text-center">{risk.riskScore}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="risks" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Risk Register</span>
                  <div className="flex gap-2">
                    <Select value={filter.status} onValueChange={(value) => setFilter({...filter, status: value})}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="mitigated">Mitigated</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filter.category} onValueChange={(value) => setFilter({...filter, category: value})}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="Financial">Financial</SelectItem>
                        <SelectItem value="Technical">Technical</SelectItem>
                        <SelectItem value="Resources">Resources</SelectItem>
                        <SelectItem value="Compliance">Compliance</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button variant="outline">
                      <Filter className="h-4 w-4 mr-2" />
                      More Filters
                    </Button>
                    
                    <Button onClick={addRisk}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Risk
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Add New Risk</h3>
                    <div className="space-y-2">
                      <Label htmlFor="riskTitle">Risk Title</Label>
                      <Input
                        id="riskTitle"
                        placeholder="Enter risk title"
                        value={newRisk.title}
                        onChange={(e) => setNewRisk({...newRisk, title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="riskCategory">Category</Label>
                      <Select value={newRisk.category} onValueChange={(value) => setNewRisk({...newRisk, category: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Financial">Financial</SelectItem>
                          <SelectItem value="Technical">Technical</SelectItem>
                          <SelectItem value="Resources">Resources</SelectItem>
                          <SelectItem value="Compliance">Compliance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="riskDescription">Description</Label>
                      <Textarea
                        id="riskDescription"
                        placeholder="Describe the risk"
                        value={newRisk.description}
                        onChange={(e) => setNewRisk({...newRisk, description: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="probability">Probability (1-5)</Label>
                        <Input
                          id="probability"
                          type="number"
                          min="1"
                          max="5"
                          value={newRisk.probability}
                          onChange={(e) => setNewRisk({...newRisk, probability: parseInt(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="impact">Impact (1-5)</Label>
                        <Input
                          id="impact"
                          type="number"
                          min="1"
                          max="5"
                          value={newRisk.impact}
                          onChange={(e) => setNewRisk({...newRisk, impact: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="owner">Risk Owner</Label>
                      <Input
                        id="owner"
                        placeholder="Enter risk owner"
                        value={newRisk.owner}
                        onChange={(e) => setNewRisk({...newRisk, owner: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mitigationPlan">Mitigation Plan</Label>
                      <Textarea
                        id="mitigationPlan"
                        placeholder="Describe mitigation plan"
                        value={newRisk.mitigationPlan}
                        onChange={(e) => setNewRisk({...newRisk, mitigationPlan: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Risk Score Calculator</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary">
                          {newRisk.probability * newRisk.impact}
                        </div>
                        <p className="text-sm text-muted-foreground">Risk Score</p>
                        <Badge className={getRiskLevel(newRisk.probability * newRisk.impact).color}>
                          {getRiskLevel(newRisk.probability * newRisk.impact).level}
                        </Badge>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between">
                          <span>Probability:</span>
                          <span>{newRisk.probability}/5</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Impact:</span>
                          <span>{newRisk.impact}/5</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Current Risks</h3>
                  <div className="space-y-4">
                    {filteredRisks.map((risk) => {
                      const riskLevel = getRiskLevel(risk.riskScore);
                      return (
                        <Card key={risk.id}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold">{risk.title}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{risk.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-sm">
                                  <span>Category: {risk.category}</span>
                                  <span>Owner: {risk.owner}</span>
                                  <span>Identified: {new Date(risk.dateIdentified).toLocaleDateString()}</span>
                                </div>
                                <p className="text-sm mt-2">
                                  <strong>Mitigation:</strong> {risk.mitigationPlan}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-sm">P: {risk.probability}</span>
                                  <span className="text-sm">I: {risk.impact}</span>
                                  <span className="text-sm font-semibold">Score: {risk.riskScore}</span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 ml-4">
                                <Badge className={riskLevel.color}>
                                  {riskLevel.level}
                                </Badge>
                                <Badge className={getStatusColor(risk.status)}>
                                  {risk.status}
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="matrix" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Risk Assessment Matrix</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="border p-2 text-center bg-gray-100">Impact →<br />Probability ↓</th>
                        <th className="border p-2 text-center bg-green-100">1 - Very Low</th>
                        <th className="border p-2 text-center bg-yellow-100">2 - Low</th>
                        <th className="border p-2 text-center bg-orange-100">3 - Medium</th>
                        <th className="border p-2 text-center bg-red-100">4 - High</th>
                        <th className="border p-2 text-center bg-red-200">5 - Very High</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[5, 4, 3, 2, 1].map((probability) => (
                        <tr key={probability}>
                          <td className="border p-2 text-center font-semibold bg-gray-100">
                            {probability} - {['Very Low', 'Low', 'Medium', 'High', 'Very High'][probability - 1]}
                          </td>
                          {[1, 2, 3, 4, 5].map((impact) => {
                            const score = probability * impact;
                            const riskLevel = getRiskLevel(score);
                            const cellRisks = risks.filter(r => r.probability === probability && r.impact === impact);
                            
                            return (
                              <td key={impact} className={`border p-2 text-center ${riskLevel.color.replace('text-', 'bg-').replace('-800', '-200')}`}>
                                <div className="font-bold">{score}</div>
                                <div className="text-xs">{riskLevel.level}</div>
                                {cellRisks.length > 0 && (
                                  <div className="text-xs mt-1">
                                    {cellRisks.length} risk{cellRisks.length > 1 ? 's' : ''}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-green-100 rounded-lg text-center">
                    <div className="font-semibold text-green-800">Low Risk</div>
                    <div className="text-sm text-green-700">Score: 1-5</div>
                    <div className="text-xs text-green-600">Monitor</div>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-lg text-center">
                    <div className="font-semibold text-yellow-800">Medium Risk</div>
                    <div className="text-sm text-yellow-700">Score: 6-9</div>
                    <div className="text-xs text-yellow-600">Manage</div>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg text-center">
                    <div className="font-semibold text-orange-800">High Risk</div>
                    <div className="text-sm text-orange-700">Score: 10-14</div>
                    <div className="text-xs text-orange-600">Mitigate</div>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg text-center">
                    <div className="font-semibold text-red-800">Critical Risk</div>
                    <div className="text-sm text-red-700">Score: 15-25</div>
                    <div className="text-xs text-red-600">Urgent Action</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Risk Management Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Risk Register Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Risk Matrix Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Mitigation Status Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Executive Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Trend Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Action Plan Report</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
